import type { PlanData } from "../../types/SubscriptionEmbed";
import { isEmpty } from "../../utils/isEmpty";
import { formatRequiredFields } from "../headlessSDK/utils";
import { convertKeysToCamelCase } from "./convertKeysToCamelCase";
import { getIntentStateDetails } from "./getIntentStateDetails";
import { sdkSelect } from "./sdkVersionSelect";

export const getSDKAllowedPaymentEventData = ({
  intent,
  selectedMethodSlug,
  actionData,
  intentSdkState: _intentSdkState,
  nextAction,
  lastUsedMethod,
  planDetails,
  subscription,
}: {
  intent: PaymentIntent;
  selectedMethodSlug: PaymentMethodSlugs | null;
  actionData: PaymentActionData;
  intentSdkState: IntentSdkState;
  nextAction: NextActions;
  partialWallet?: boolean;
  lastUsedMethod?: LastUsedMethod;
  planDetails?: PlanData;
  subscription?: SubscriptionDetails | null;
}) => {
  const intentSdkState = resolveIntentSdkState({
    sdkState: _intentSdkState,
    actionData,
    nextAction,
  });

  const { transaction, redirect, provider_id } = actionData;
  return {
    intent: {
      id: intent.id,
      status: intent.status,
      amount: {
        value: intent.amount,
        currency: intent.amount_currency,
        formatted: intent.formatted_amount,
      },
      subtotalAmount: intent.subtotal_amount,
      fees: intent.fees,
      totalDiscounts: intent.total_discounts,
      expirationDate: intent.expiration_date || null,
      secret: intent.secret,
      isLive: intent.is_live,
    },
    paymentStatus: intent.payment_status
      ? {
          status: intent.payment_status.status,
          balances: convertKeysToCamelCase(intent.payment_status.balances),
        }
      : null,
    lastUsedMethod: lastUsedMethod || null,
    selectedMethod: selectedMethodSlug,
    customerId: intent.customer || null,
    __providerId__: provider_id || null,
    transaction: !isEmpty(transaction)
      ? {
          id: transaction?.id,
          status: transaction?.trx_status,
          responseCode: transaction.operations[0].latest_status.code,
          responseMessage: transaction.operations[0].latest_status.message,
          localizedResponseMessage:
            transaction.operations[0].latest_status.localized_message,
          operations: transaction.operations,
          createdDate: transaction?.created,
          amount: {
            value:
              typeof transaction.amount === "number"
                ? transaction.amount
                : transaction.amount.formatted,
            currency: transaction.amount_currency,
          },
          paymentMethod: transaction?.payment_method,
          paymentMethodName: transaction?.payment_method_name,
          billingData: transaction?.billing_data,
          customFields: transaction?.custom_fields,
          providerTransactionFields: transaction?.provider_transaction_fields,
          customFormAnswers: transaction?.custom_form_answers
            ? {
                formFields: transaction.custom_form_answers.form_fields,
              }
            : null,
          externalActionMessage: transaction?.external_action_message,
          ...(selectedMethodSlug === "TABBY"
            ? { methodErrorMessage: transaction?.method_error_message ?? null }
            : {}),
        }
      : null,
    state: intentSdkState,
    shippingData: actionData?.shipping_data || null,
    productItems: actionData?.product_items_data || null,
    nativePayData: actionData?.native_pay_data || null,
    __nativePayData__: actionData?.native_pay_data || null, // old versions
    recommendedMethods: (actionData as RecommendMethodsActionData)
      .payment_methods
      ? (actionData as RecommendMethodsActionData).payment_methods.map(
          (method) => ({
            id: method.payment_method,
            title: method.payment_method_name,
            icons: method.checkout_icons,
            confirmationRequired: method.confirmation_required,
            requiredBillingFields:
              method.required_billing_fields?.length > 0
                ? formatRequiredFields(method.required_billing_fields)
                : null,
            requiredShippingFields:
              method.required_shipping_fields?.length > 0
                ? formatRequiredFields(method.required_shipping_fields)
                : null,
          })
        )
      : null,
    ...sdkSelect({
      0: {
        redirect: redirect
          ? {
              redirectUrl: redirect.redirect_url,
            }
          : null,
      },
      default: {
        stateDetails: getIntentStateDetails({
          intentSdkState,
          actionData,
          nextAction,
          currency: intent.amount_currency,
        }),
      },
    }),
    subscription: planDetails
      ? {
          plan: convertKeysToCamelCase(planDetails),
          status: subscription?.current_status.value,
          id: subscription?.id || null,
        }
      : null,
  };
};

export const getSDKAllowedPayoutEventData = ({
  intent,
  selectedMethodSlug,
  actionData,
  intentSdkState,
}: {
  intent: PayoutIntent;
  selectedMethodSlug: PaymentMethodSlugs | null;
  actionData: PayoutActionData;
  intentSdkState: IntentSdkState;
}) => {
  const { transaction } = actionData;

  return {
    intent: {
      id: intent.id,
      status: intent.status,
      amount: {
        value: `${intent.amount}${intent.amount_currency}`,
        currency: intent.amount_currency,
        formatted: intent.amount,
        maxPayout: intent.max_payout_amount || null,
      },
      secret: intent.secret,
      isLive: intent.is_live,
    },
    selectedMethod: selectedMethodSlug,
    transaction: transaction
      ? {
          id: transaction?.id,
          status: transaction?.trx_status,
          operations: transaction.operations,
          createdDate: transaction?.created,
          payoutMethod: transaction?.pay_out_method,
          payoutMethodName: transaction?.payout_method_name,
          billingData: transaction?.billing_data,
          customFields: transaction?.custom_fields,
          externalActionMessage: transaction?.external_action_message,
          providerTransactionFields: transaction?.provider_transaction_fields,
        }
      : null,
    state: intentSdkState,
  };
};

function resolveIntentSdkState({
  actionData,
  sdkState,
  nextAction,
}: {
  sdkState: IntentSdkState;
  actionData: PaymentActionData;
  nextAction: NextActions;
}): IntentSdkState {
  /**
   * CKO doesn't support auto capture, BE respond with checkout redirect URL
   * https://github.com/MoneyHash/one/pull/8950
   */
  if (
    sdkState === "URL_TO_RENDER" &&
    actionData?.redirect?.redirect_url?.includes("embed.moneyhash.io")
  ) {
    return "PROCESSING";
  }

  // ApplePay MoneyHash hosted skip intermediate native pay loading, and redirect to ApplePay MoneyHash hosted page
  if (
    nextAction === "NATIVE_PAY" &&
    actionData?.is_merchant_hosted === false &&
    actionData?.js_sdk_url_to_render_enabled
  ) {
    return "URL_TO_RENDER";
  }

  return sdkState;
}
